import {
  createWalletClient,
  custom,
  type Address,
  type EIP1193Provider,
} from "viem";

import { publicClient, targetChain } from "@stonkland/protocol/chain";
import {
  addresses,
  activationAbi,
  ammAbi,
  boosterAbi,
  brokerAbi,
  erc20Abi,
  loanVaultAbi,
  overtimeBoosterAbi,
} from "@stonkland/protocol/contracts";
import {
  LAND_SWAP_GAS_RESERVE,
  buildLandSwapPlan,
  permit2Abi,
  quoteEthToLand,
  quoteLandToEth,
  requireLandPoolRuntime,
  universalRouterAbi,
} from "@web/lib/land-swap";

const requireAddress = (address: Address | undefined, label: string) => {
  if (!address) throw new Error(`${label} contract is not configured.`);
  return address;
};

const connectedWallet = (provider: EIP1193Provider, account: Address) =>
  createWalletClient({
    account,
    chain: targetChain,
    transport: custom(provider),
  });

type ConnectedWallet = ReturnType<typeof connectedWallet>;

export type TransactionProgress = {
  hash?: `0x${string}`;
  label: string;
  stage: "awaiting_signature" | "submitted" | "confirmed";
};

type TransactionProgressReporter = (progress: TransactionProgress) => void;

export class ProtocolTransactions {
  constructor(
    private readonly wallet: ConnectedWallet,
    private readonly account: Address,
    private reportProgress?: TransactionProgressReporter,
  ) {}

  setProgressReporter(reporter?: TransactionProgressReporter) {
    this.reportProgress = reporter;
  }

  private async writeAndConfirm(
    label: string,
    write: () => Promise<`0x${string}`>,
  ) {
    this.reportProgress?.({ label, stage: "awaiting_signature" });
    const hash = await write();
    this.reportProgress?.({ hash, label, stage: "submitted" });
    const receipt = await publicClient.waitForTransactionReceipt({ hash });
    if (receipt.status !== "success") {
      throw new Error(`Transaction ${hash} reverted.`);
    }
    this.reportProgress?.({ hash, label, stage: "confirmed" });
    return hash;
  }

  private async approveLand(spender: Address, amount: bigint) {
    const land = requireAddress(addresses.land, "LAND");
    await this.writeAndConfirm("Approve LAND", () =>
      this.wallet.writeContract({
        account: this.wallet.account,
        chain: this.wallet.chain,
        address: land,
        abi: erc20Abi,
        functionName: "approve",
        args: [spender, amount],
      }),
    );
  }

  private async approveBroker(spender: Address, tokenId: bigint) {
    const brokers = requireAddress(addresses.brokers, "BrokerNFT");
    await this.writeAndConfirm("Approve Broker", () =>
      this.wallet.writeContract({
        account: this.wallet.account,
        chain: this.wallet.chain,
        address: brokers,
        abi: brokerAbi,
        functionName: "approve",
        args: [spender, tokenId],
      }),
    );
  }

  async assignWork(options: {
    tokenId: bigint;
    tier: number;
  }) {
    const activation = requireAddress(
      addresses.activation,
      "ActivationManager",
    );
    const feeDue = await publicClient.readContract({
      address: activation,
      abi: activationAbi,
      functionName: "quoteActivation",
      args: [options.tokenId, options.tier],
    });
    await this.approveLand(activation, feeDue);
    return this.writeAndConfirm("Confirm work assignment", () =>
      this.wallet.writeContract({
        account: this.wallet.account,
        chain: this.wallet.chain,
        address: activation,
        abi: activationAbi,
        functionName: "activate",
        args: [options.tokenId, options.tier],
      }),
    );
  }

  async assignWorkBatch(options: { tokenIds: bigint[]; tier: number }) {
    if (options.tokenIds.length === 0) {
      throw new Error("Select at least one Broker for batch activation.");
    }
    const activation = requireAddress(
      addresses.activation,
      "ActivationManager",
    );
    const tiers = options.tokenIds.map(() => options.tier);
    const totalFee = await publicClient.readContract({
      address: activation,
      abi: activationAbi,
      functionName: "quoteActivationBatch",
      args: [options.tokenIds, tiers],
    });
    await this.approveLand(activation, totalFee);
    const hash = await this.writeAndConfirm("Confirm batch assignment", () =>
      this.wallet.writeContract({
        account: this.wallet.account,
        chain: this.wallet.chain,
        address: activation,
        abi: activationAbi,
        functionName: "activateBatch",
        args: [options.tokenIds, tiers],
      }),
    );
    return { hash, totalFee };
  }

  async buyBroker(options: { tokenId?: bigint }) {
    const amm = requireAddress(addresses.amm, "BrokerAMM");
    const tokenId = options.tokenId;
    if (tokenId !== undefined) {
      const initialQuote = await publicClient.readContract({
        address: amm,
        abi: ammAbi,
        functionName: "quoteSpecificBuy",
        args: [tokenId],
      });
      if (!initialQuote[2]) {
        throw new Error(`Broker #${tokenId} is not in AMM inventory.`);
      }
      await this.approveLand(amm, initialQuote[0]);
      const quote = await publicClient.readContract({
        address: amm,
        abi: ammAbi,
        functionName: "quoteSpecificBuy",
        args: [tokenId],
      });
      if (!quote[2]) {
        throw new Error(`Broker #${tokenId} left AMM inventory before signing.`);
      }
      const hash = await this.writeAndConfirm("Buy exact Broker", () =>
        this.wallet.writeContract({
          account: this.wallet.account,
          chain: this.wallet.chain,
          address: amm,
          abi: ammAbi,
          functionName: "buySpecificNFT",
          args: [tokenId, quote[0]],
          value: quote[1],
        }),
      );
      return { hash, tokenCost: quote[0], nativeFee: quote[1] };
    }
    const initialQuote = await publicClient.readContract({
      address: amm,
      abi: ammAbi,
      functionName: "quoteRandomBuy",
    });
    if (initialQuote[2] === 0n) throw new Error("AMM inventory is empty.");
    await this.approveLand(amm, initialQuote[0]);
    const quote = await publicClient.readContract({
      address: amm,
      abi: ammAbi,
      functionName: "quoteRandomBuy",
    });
    if (quote[2] === 0n) throw new Error("AMM inventory emptied before signing.");
    const hash = await this.writeAndConfirm("Buy next Broker", () =>
      this.wallet.writeContract({
        account: this.wallet.account,
        chain: this.wallet.chain,
        address: amm,
        abi: ammAbi,
        functionName: "buyRandomNFT",
        args: [quote[0]],
        value: quote[1],
      }),
    );
    return {
      hash,
      tokenId: quote[3],
      tokenCost: quote[0],
      nativeFee: quote[1],
    };
  }

  async buyLand(options: { amountIn: bigint; slippageBps: number }) {
    const [quote, nativeBalance, block] = await Promise.all([
      quoteEthToLand(options.amountIn, options.slippageBps),
      publicClient.getBalance({ address: this.account }),
      publicClient.getBlock(),
    ]);
    if (nativeBalance < options.amountIn + LAND_SWAP_GAS_RESERVE) {
      throw new Error(
        "Insufficient ETH. Keep at least 0.001 ETH available for network gas.",
      );
    }

    const plan = buildLandSwapPlan(quote);
    const deadline = block.timestamp + 20n * 60n;
    const hash = await this.writeAndConfirm("Buy LAND with ETH", () =>
      this.wallet.writeContract({
        account: this.wallet.account,
        chain: this.wallet.chain,
        address: plan.runtime.universalRouter,
        abi: universalRouterAbi,
        functionName: "execute",
        args: [plan.commands, plan.inputs, deadline],
        value: options.amountIn,
      }),
    );
    return { hash, ...quote };
  }

  async sellLand(options: { amountIn: bigint; slippageBps: number }) {
    const land = requireAddress(addresses.land, "LAND");
    const [landBalance, nativeBalance] = await Promise.all([
      publicClient.readContract({
        address: land,
        abi: erc20Abi,
        functionName: "balanceOf",
        args: [this.account],
      }),
      publicClient.getBalance({ address: this.account }),
    ]);
    if (landBalance < options.amountIn) {
      throw new Error("Insufficient LAND balance for this sale.");
    }
    if (nativeBalance < LAND_SWAP_GAS_RESERVE) {
      throw new Error(
        "Insufficient ETH. Keep at least 0.001 ETH available for network gas.",
      );
    }

    const runtime = requireLandPoolRuntime();
    const firstBlock = await publicClient.getBlock();
    const approvalExpiration = firstBlock.timestamp + 30n * 60n;
    const [tokenAllowance, permit2Allowance] = await Promise.all([
      publicClient.readContract({
        address: land,
        abi: erc20Abi,
        functionName: "allowance",
        args: [this.account, runtime.permit2],
      }),
      publicClient.readContract({
        address: runtime.permit2,
        abi: permit2Abi,
        functionName: "allowance",
        args: [this.account, land, runtime.universalRouter],
      }),
    ]);
    if (tokenAllowance < options.amountIn) {
      await this.approveLand(runtime.permit2, options.amountIn);
    }
    if (
      permit2Allowance[0] < options.amountIn ||
      BigInt(permit2Allowance[1]) < approvalExpiration
    ) {
      await this.writeAndConfirm("Authorize Uniswap router", () =>
        this.wallet.writeContract({
          account: this.wallet.account,
          chain: this.wallet.chain,
          address: runtime.permit2,
          abi: permit2Abi,
          functionName: "approve",
          args: [
            land,
            runtime.universalRouter,
            options.amountIn,
            Number(approvalExpiration),
          ],
        }),
      );
    }

    const [quote, block] = await Promise.all([
      quoteLandToEth(options.amountIn, options.slippageBps),
      publicClient.getBlock(),
    ]);
    const deadline = block.timestamp + 20n * 60n;
    const plan = buildLandSwapPlan(quote);
    const hash = await this.writeAndConfirm("Sell LAND for ETH", () =>
      this.wallet.writeContract({
        account: this.wallet.account,
        chain: this.wallet.chain,
        address: plan.runtime.universalRouter,
        abi: universalRouterAbi,
        functionName: "execute",
        args: [plan.commands, plan.inputs, deadline],
      }),
    );
    return { hash, ...quote };
  }

  async sellBroker(options: { tokenId: bigint }) {
    const amm = requireAddress(addresses.amm, "BrokerAMM");
    await this.approveBroker(amm, options.tokenId);
    const quote = await publicClient.readContract({
      address: amm,
      abi: ammAbi,
      functionName: "quoteSellNFT",
    });
    const hash = await this.writeAndConfirm("Sell Broker", () =>
      this.wallet.writeContract({
        account: this.wallet.account,
        chain: this.wallet.chain,
        address: amm,
        abi: ammAbi,
        functionName: "sellNFT",
        args: [options.tokenId, quote[0]],
        value: quote[1],
      }),
    );
    return { hash, tokenPayout: quote[0], nativeFee: quote[1] };
  }

  async clockIn() {
    const booster = requireAddress(addresses.stockBooster, "StockBooster");
    const [pot, stocks, roundActive] = await Promise.all([
      publicClient.readContract({ address: booster, abi: boosterAbi, functionName: "pendingEth" }),
      publicClient.readContract({ address: booster, abi: boosterAbi, functionName: "getStockTokens" }),
      publicClient.readContract({ address: booster, abi: boosterAbi, functionName: "roundActive" }),
    ]);
    if (stocks.some((stock) => /^0x0{40}$/i.test(stock))) throw new Error("The onchain Clock In stock lineup is not configured.");
    if (!roundActive) {
      await this.writeAndConfirm("Start Clock In round", () =>
        this.wallet.writeContract({
          account: this.wallet.account,
          chain: this.wallet.chain,
          address: booster,
          abi: boosterAbi,
          functionName: "startDropPublic",
          args: [BigInt(Math.floor(Date.now() / 1000) + 10 * 60)],
        }),
      );
    }
    const continueHash = await this.writeAndConfirm(
      "Distribute Clock In round",
      () =>
        this.wallet.writeContract({
          account: this.wallet.account,
          chain: this.wallet.chain,
          address: booster,
          abi: boosterAbi,
          functionName: "continueDrop",
          args: [100n],
        }),
    );
    return {
      hash: continueHash,
      nativeAmount: pot,
      routeCount: stocks.length,
    };
  }

  async overtime() {
    const booster = requireAddress(addresses.overtimeBooster, "OvertimeBooster");
    const [pot, stocks, roundActive] = await Promise.all([
      publicClient.readContract({ address: booster, abi: overtimeBoosterAbi, functionName: "pendingEth" }),
      publicClient.readContract({ address: booster, abi: overtimeBoosterAbi, functionName: "getStockTokens" }),
      publicClient.readContract({ address: booster, abi: overtimeBoosterAbi, functionName: "roundActive" }),
    ]);
    if (stocks.length === 0) throw new Error("The Overtime stock lineup is not configured.");
    if (!roundActive) {
      await this.writeAndConfirm("Start Overtime round", () =>
        this.wallet.writeContract({
          account: this.wallet.account,
          chain: this.wallet.chain,
          address: booster,
          abi: overtimeBoosterAbi,
          functionName: "startDropPublic",
          args: [BigInt(Math.floor(Date.now() / 1000) + 10 * 60)],
        }),
      );
    }
    const hash = await this.writeAndConfirm("Distribute Overtime round", () =>
      this.wallet.writeContract({
        account: this.wallet.account,
        chain: this.wallet.chain,
        address: booster,
        abi: overtimeBoosterAbi,
        functionName: "continueDrop",
        args: [100n],
      }),
    );
    return { hash, nativeAmount: pot, routeCount: stocks.length };
  }

  async borrow(options: { tokenId: bigint; duration: bigint }) {
    const vault = requireAddress(addresses.loanVault, "StonkLoanVault");
    await this.approveBroker(vault, options.tokenId);
    // Approval is a separately mined transaction. Quote only after it confirms so the value
    // presented for the borrow signature reflects the latest configured AMM notional. A refundable
    // buffer also covers a governance update while the signed borrow waits to be mined.
    const fee = await publicClient.readContract({ address: vault, abi: loanVaultAbi, functionName: "quoteBorrowFee", args: [options.duration] });
    const minimumBuffer = 100_000_000_000_000n;
    const feeBuffer = fee / 10n > minimumBuffer ? fee / 10n : minimumBuffer;
    return this.writeAndConfirm("Open Broker loan", () =>
      this.wallet.writeContract({
        account: this.wallet.account,
        chain: this.wallet.chain,
        address: vault,
        abi: loanVaultAbi,
        functionName: "borrow",
        args: [options.tokenId, options.duration],
        value: fee + feeBuffer,
      }),
    );
  }

  async repay(loanId: bigint) {
    const vault = requireAddress(addresses.loanVault, "StonkLoanVault");
    const principal = await publicClient.readContract({ address: vault, abi: loanVaultAbi, functionName: "tokensPerNFT" });
    await this.approveLand(vault, principal);
    // Late fees grow with time and the configured AMM notional. Re-quote after approval, then include
    // a bounded signing/mining buffer. The vault refunds every unused wei atomically.
    const lateFee = await publicClient.readContract({ address: vault, abi: loanVaultAbi, functionName: "quoteLateFee", args: [loanId] });
    const minimumBuffer = 100_000_000_000_000n; // 0.0001 ETH covers maturity-boundary drift.
    const feeBuffer = lateFee / 10n > minimumBuffer ? lateFee / 10n : minimumBuffer;
    return this.writeAndConfirm("Repay Broker loan", () =>
      this.wallet.writeContract({ account: this.wallet.account, chain: this.wallet.chain, address: vault, abi: loanVaultAbi, functionName: "repay", args: [loanId], value: lateFee + feeBuffer }),
    );
  }

  async liquidate(loanId: bigint) {
    const vault = requireAddress(addresses.loanVault, "StonkLoanVault");
    return this.writeAndConfirm("Liquidate expired loan", () =>
      this.wallet.writeContract({ account: this.wallet.account, chain: this.wallet.chain, address: vault, abi: loanVaultAbi, functionName: "liquidate", args: [loanId] }),
    );
  }

}

export const createProtocolTransactions = (
  provider: EIP1193Provider,
  account: Address,
) => new ProtocolTransactions(connectedWallet(provider, account), account);
